import { type ReactNode, useMemo } from 'react';
import { useToggleHiddenInstances } from '@/pages/Schedule/hooks';
import type { DecoratedInstance } from '@/pages/Schedule/types';
import { getEntryInstances, sortEntryInstances } from '@/pages/Schedule/utils';
import { areDaysEqual } from '@/utils/dates';
import { useScheduleContext } from '../ScheduleContext';
import { EntriesContext } from './EntriesContext';
import { useDraftEntry } from './hooks/useDraftEntry';
import { useEntries } from './hooks/useEntries';
import { useEntriesAddedSubscription } from './hooks/useEntriesAddedSubscription';
import { useEntryAddedSubscription } from './hooks/useEntryAddedSubscription';
import { useEntryDeletedSubscription } from './hooks/useEntryDeletedSubscription';
import { useEntryUpdatedSubscription } from './hooks/useEntryUpdatedSubscription';
import type {
  EntriesContextOptions,
  EntriesContextValue,
  EntriesMap,
} from './types';
import { getEntryInstancesWithDraft } from './utils';

type EntriesContextProviderProps = {
  children: ReactNode | ((value: EntriesContextValue) => ReactNode);
  options?: Partial<EntriesContextOptions>;
};

export const EntriesContextProvider = ({
  children,
  options: _options,
}: EntriesContextProviderProps) => {
  const options: EntriesContextOptions = {
    includeMessages: _options?.includeMessages ?? false,
    includeRsvps: _options?.includeRsvps ?? false,
  };

  const { isShowingHiddenInstances } = useToggleHiddenInstances();
  const { draftEntry, draftMode, draftComposer } = useDraftEntry();
  const {
    scheduleId,
    visibleStartDate,
    endDate,
    timeZone: scheduleTimeZone,
  } = useScheduleContext();

  // Ensure on-day entries are included when searching for a single day. Views
  // supply an exclusive end, so a single-day window would otherwise collapse to
  // an empty range for both fetching and recurrence expansion.
  const adjustedEndDate = useMemo(() => {
    return areDaysEqual(visibleStartDate, endDate)
      ? endDate.plus({ days: 1 })
      : endDate;
  }, [visibleStartDate, endDate]);

  // Do not use spreads here for performance
  const {
    entries,
    error,
    isLoading,
    isFetching,
    isSuccess,
    isPlaceholderData,
    prefetchData,
  } = useEntries(visibleStartDate, adjustedEndDate, options);

  useEntryAddedSubscription(scheduleId);
  useEntriesAddedSubscription(scheduleId);
  useEntryUpdatedSubscription(scheduleId, options);
  useEntryDeletedSubscription(scheduleId);

  // Expand entries coming from the API
  const serverEntryInstances = useMemo<DecoratedInstance[]>(() => {
    return entries.reduce<DecoratedInstance[]>((instances, entry) => {
      const expandedInstances = getEntryInstances(
        entry,
        visibleStartDate,
        adjustedEndDate,
        scheduleTimeZone
      );
      return instances.concat(expandedInstances);
    }, []);
  }, [entries, visibleStartDate, adjustedEndDate, scheduleTimeZone]);

  // Add the draft to our existing set of instances and sort them
  // This logic is decoupled from `serverEntryInstances` to avoid re-computing
  // server data whenever the draft changes.
  const sortedEntryInstancesWithDraft = useMemo<DecoratedInstance[]>(() => {
    const instances = draftEntry
      ? getEntryInstancesWithDraft(
          serverEntryInstances,
          draftEntry,
          visibleStartDate,
          adjustedEndDate,
          scheduleTimeZone
        )
      : serverEntryInstances;
    return sortEntryInstances(instances, draftEntry, draftMode, draftComposer);
  }, [
    draftEntry,
    serverEntryInstances,
    visibleStartDate,
    adjustedEndDate,
    draftMode,
    draftComposer,
    scheduleTimeZone,
  ]);

  // This is exported as `entryInstances`.
  const visibleEntryInstances = useMemo<DecoratedInstance[]>(() => {
    return isShowingHiddenInstances
      ? sortedEntryInstancesWithDraft
      : sortedEntryInstancesWithDraft.filter(({ isHidden }) => !isHidden);
  }, [sortedEntryInstancesWithDraft, isShowingHiddenInstances]);

  const entriesMap = useMemo(() => {
    const entriesMap = entries.reduce<EntriesMap>((acc, entry) => {
      acc[entry.id] = entry;
      return acc;
    }, {});

    if (draftEntry) {
      entriesMap[draftEntry.id] = draftEntry;
    }

    return entriesMap;
  }, [entries, draftEntry]);

  const value: EntriesContextValue = useMemo(
    () => ({
      entries,
      entriesMap,
      error,
      isLoading,
      isFetching,
      isSuccess,
      isPlaceholderData,
      prefetchData,
      allEntryInstances: sortedEntryInstancesWithDraft,
      entryInstances: visibleEntryInstances,
    }),
    [
      entries,
      entriesMap,
      error,
      isLoading,
      isFetching,
      isSuccess,
      isPlaceholderData,
      prefetchData,
      sortedEntryInstancesWithDraft,
      visibleEntryInstances,
    ]
  );

  return (
    <EntriesContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </EntriesContext.Provider>
  );
};
